import { useTranslation } from 'react-i18next';
import type { ResetCredentialMode } from './useResetCredential';

export interface BulkResetResultRow {
    sub: string;
    username: string;
    temp_password?: string;
    ok: boolean;
    error?: string;
}

interface Props {
    results: BulkResetResultRow[];
    /** EAA-1779: which credential the run used — decides whether there is a secret to list. */
    mode: ResetCredentialMode;
    onDismiss: () => void;
}

function toCsv(rows: BulkResetResultRow[]): string {
    const header = 'username,temp_password\n';
    const body = rows
        .filter((r) => r.ok && r.temp_password)
        .map((r) => `${r.username},${r.temp_password}`)
        .join('\n');
    return header + body;
}

/**
 * EAA-1820: "Bulk reset produces one downloadable list, shown once — never
 * emailed from the portal." `POST /accounts/reset-password` is single-account
 * only (see `AccountResetPasswordSerializer`); a bulk reset is this page
 * looping that call per selected sub and collecting the one-time temp
 * passwords into ONE list here, rendered once, with an explicit
 * "Đã lưu xong — ẩn thông tin" dismiss — same one-time-secret contract as the
 * single-account reset panel already on this page, just N rows instead of 1.
 *
 * EAA-1779: in `admin_set` mode there is no secret to collect — every account
 * got the password the operator typed, and the server never sends it back — so
 * the panel drops the password column and the CSV download entirely and reports
 * only which rows landed. Rendering an empty password column there would read
 * as "the reset produced nothing".
 */
export function BulkResetResultPanel({ results, mode, onDismiss }: Props) {
    const { t } = useTranslation();
    const okRows = results.filter((r) => r.ok);
    const failedRows = results.filter((r) => !r.ok);
    const revealsSecrets = mode !== 'admin_set';

    function download() {
        const blob = new Blob([toCsv(results)], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mat-khau-tam-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    return (
        <div className="portal-admin-secret" role="alert">
            <strong>
                {revealsSecrets
                    ? t('console.accounts.bulkResetResult.heading', { count: okRows.length })
                    : t('console.accounts.bulkResetResult.headingAdminSet', { count: okRows.length })}
            </strong>
            <div className="portal-table-scroll">
                <table className="portal-admin-table">
                    <thead>
                        <tr>
                            <th>{t('console.accounts.bulkResetResult.columnUsername')}</th>
                            {revealsSecrets && (
                                <th>{t('console.accounts.bulkResetResult.columnTempPassword')}</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {okRows.map((r) => (
                            <tr key={r.sub}>
                                <td>
                                    <code>{r.username}</code>
                                </td>
                                {revealsSecrets && (
                                    <td>
                                        <code>{r.temp_password}</code>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {failedRows.length > 0 && (
                <p className="portal-admin-error">
                    {t('console.accounts.bulkResetResult.failedNotice', {
                        count: failedRows.length,
                        usernames: failedRows.map((r) => r.username).join(', '),
                    })}
                </p>
            )}
            <div className="portal-admin-form-actions">
                {revealsSecrets && (
                    <button type="button" className="portal-btn" onClick={download} disabled={okRows.length === 0}>
                        {t('console.accounts.bulkResetResult.downloadButton')}
                    </button>
                )}
                <button type="button" onClick={onDismiss}>
                    {t('console.accounts.bulkResetResult.dismissButton')}
                </button>
            </div>
        </div>
    );
}
